import { motion } from 'motion/react';
import { SUIT_GLYPHS, isRed, rankLabel, rankOf, suitOf, type CardId } from '@ubs/rules';

export type Direction = 'left' | 'right';

/**
 * Rakip kartının masaya uçarak geldiği başlangıç noktası — yığının merkezine
 * göre piksel cinsinden sapma.
 *
 * Her kart bu noktadan uçuyor: kendiminki elde durduğu yerden (tıklama anında
 * ölçülüyor), rakibinki oturduğu kenardan. Sapma SABİT VERİLEMEZ: masa büyüyüp
 * küçülüyor, sabit değerde kart oyuncunun yerinden değil ortadan doğuyor.
 * Bu yüzden `Table` oyun alanını ölçüp buraya geçiriyor.
 */
export interface Entry {
  x: number;
  y: number;
  rotate: number;
}

interface Props {
  card: CardId;
  selected?: boolean | undefined;
  /** true = yukarı kalkar (oynanabilir). undefined = vurgulama uygulanmıyor. */
  playable?: boolean | undefined;
  /**
   * Tıklanan elemanın kendisi de veriliyor: atılan kartın masaya nereden
   * uçacağı, kartın elde DURDUĞU YERDEN ölçülüyor.
   */
  onClick?: ((element: HTMLElement) => void) | undefined;
  /** Dolu ise kart bu noktadan uçarak masaya iner. */
  from?: Entry | undefined;
  /**
   * Tur bitti: kart bu yöne süzülüp sönüyor.
   *
   * Süpürme KABA DEĞİL KARTIN KENDİSİNE uygulanıyor. Kap ölçeklendiğinde
   * uçmakta olan kartın atası her karede değişiyor ve uçuş bozuluyordu —
   * kart masaya inerken büyüyor gibi görünmesinin sebebi buydu.
   */
  sweep?: { x: number; y: number } | undefined;
  /**
   * Elde yeni bir renk bloğunun ilk kartı — soluna ayırıcı boşluk giriyor.
   *
   * Kartlar eşit aralıkla üst üste binince iki renk tek bir blok gibi
   * görünüyor ve yanlış karta basılıyordu. Ayırıcı boşluk blokları gözle
   * sayılabilir hâle getiriyor.
   */
  groupStart?: boolean | undefined;
}

/**
 * Kart yüzü: değer SOL ÜST KÖŞEDE.
 *
 * Ortada olduğunda kartlar üst üste binince değerler kayboluyor. Gerçek
 * iskambil kartları bu yüzden köşe indeksli — elde yelpaze yapıldığında
 * yalnızca sol kenar görünür. Sağ altta 180° dönmüş ikinci indeks, kart
 * masada tek başına dururken gerçek kart hissini veriyor.
 */
function Face({ card }: { card: CardId }) {
  const suit = suitOf(card);
  const label = rankLabel(rankOf(card));
  const glyph = SUIT_GLYPHS[suit];
  return (
    <>
      <span className="card__index">
        <b>{label}</b>
        <i>{glyph}</i>
      </span>
      <span className="card__index card__index--flip" aria-hidden>
        <b>{label}</b>
        <i>{glyph}</i>
      </span>
    </>
  );
}

export function Card({ card, selected, playable, onClick, from, sweep, groupStart }: Props) {
  const suit = suitOf(card);
  /**
   * Kartın etrafında renkli çerçeve YOK.
   *
   * Koz kartlarını altın çerçeveyle, turu alan kartı da halkayla işaretlemek
   * denendi ve bırakıldı: masa boyalı görünüyor ve gerçek iskambil hissi
   * kayboluyordu. Bilginin tamamı başka yollarla zaten veriliyor — koz üstteki
   * göstergede, eli kimin aldığı ise kartların kazanana doğru süpürülmesinde.
   *
   * Oynanabilirlik ve seçim de yalnızca kartın YUKARI KALKMASIYLA gösteriliyor
   * — olumlu sinyal, olumsuz değil.
   */
  const className = [
    'card',
    isRed(suit) ? 'card--red' : 'card--black',
    selected ? 'card--selected' : '',
    groupStart ? 'card--group-start' : '',
  ].join(' ');

  if (from) {
    return (
      <motion.div
        className={className}
        initial={{ ...from, scale: 0.62, opacity: 0 }}
        animate={
          sweep
            ? { x: sweep.x, y: sweep.y, rotate: 0, scale: 0.82, opacity: 0 }
            : { x: 0, y: 0, rotate: 0, scale: 1, opacity: 1 }
        }
        transition={
          sweep
            ? { duration: 0.42, ease: 'easeOut' }
            : { type: 'spring', stiffness: 240, damping: 24, mass: 0.7 }
        }
      >
        <Face card={card} />
      </motion.div>
    );
  }

  /**
   * `layoutId` KULLANILMIYOR.
   *
   * Kartı elden masaya taşımak için doğal yol oydu ama kart gözle görülür
   * biçimde büyüyordu: `layoutId` iki eleman arasında konum VE boyut ölçüyor,
   * hedefin atası ise döndürülmüş ve ölçeklenen bir kutu — izdüşüm o
   * dönüşümlerin altında bozuluyor. Uçuş bunun yerine `from` ile elle
   * veriliyor (bkz. `Table` → `entryOf`).
   */
  return (
    <motion.button
      type="button"
      /**
       * `layout="position"`: bir kart atılınca kalanlar yeni yerlerine
       * SÜZÜLÜYOR, zıplamıyor. Yalnızca konum — boyut ölçülmüyor, bu yüzden
       * kartın kendi `y` animasyonuyla (kalkma) çakışmıyor.
       */
      layout="position"
      onClick={onClick ? (event) => onClick(event.currentTarget) : undefined}
      disabled={!onClick}
      className={className}
      animate={{ y: selected ? -30 : playable === true ? -16 : 0 }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
      {...(onClick ? { whileTap: { scale: 0.96 } } : {})}
    >
      <Face card={card} />
    </motion.button>
  );
}

/**
 * Kartın küçük, metin içinde kullanılabilir hâli — alışveriş panelindeki
 * "ne aldın / ne verdin" listesi için. Oynanabilir değil, yalnızca gösterge.
 */
export function CardChip({ card, big }: { card: CardId; big?: boolean }) {
  const suit = suitOf(card);
  return (
    <span
      className={[
        'chip-card',
        isRed(suit) ? 'chip-card--red' : 'chip-card--black',
        big ? 'chip-card--big' : '',
      ].join(' ')}
    >
      {rankLabel(rankOf(card))}
      <i>{SUIT_GLYPHS[suit]}</i>
    </span>
  );
}

/**
 * Rakiplerin kapalı kart sırtı.
 * Yanlardaki oyuncular için kart yatık çiziliyor (`side`): sırtın içeriği
 * olmadığı için döndürmek yerine en/boy takas etmek yeterli ve düzeni bozmuyor.
 *
 * ANİMASYONSUZ. Her sırt kendi giriş/çıkış animasyonuyla çiziliyordu; iki
 * destede 32 eleman var ve dağıtımda hepsi aynı anda canlanıyordu — telefonda
 * gözle görülür takılma. Sırtın söyleyeceği tek şey "şu kadar kart kaldı",
 * bunun için hareket gerekmiyor.
 */
export function CardBack({ side }: { side?: boolean }) {
  return <div className={`card card--back card--mini ${side ? 'card--mini-side' : ''}`} />;
}

/**
 * Yerdeki kapalı kartlar — masanın üst ortasında el boyunca duruyor.
 * Boyu `.floor__cards` içinden geliyor: oyun parçası değil, gösterge.
 */
export function FloorCards({ count }: { count: number }) {
  return (
    <div className="floor">
      <div className="floor__cards">
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className="card card--back" />
        ))}
      </div>
      <span className="floor__label">YER</span>
    </div>
  );
}
